import { Box, Card, CardContent, CardHeader, List, Typography, Stack } from '@mui/material';
import { apiKey, appStyleMode, imagesUri } from '../../helpers/clientConfigs';
import { NearestSalesPoint, Point, SalesPoint } from 'orderme-api-integration-client';
import DirectionsOutlinedIcon from '@mui/icons-material/DirectionsOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { CardElement } from './cardElement';
import { ImageLoader } from '../imgLoader/imgLoader';
import { useTranslation } from 'react-i18next';
import { AppStyle } from '../../helpers/appStyle';
import { HtmlViewer } from '../../controls/html/htmlViewer';
import { useEffect, useState } from 'react';
import FormadaLogo from '../../logo/logo-formada-black.png';
import Grid from '@mui/material/Grid';

type MapType = 'call' | 'location' | 'direction';
export interface PointInfoProps {
  salesPoint: NearestSalesPoint;
}

export function PointInfo(props: PointInfoProps) {
  const { salesPoint } = props;
  const { t } = useTranslation(['salesPoint']);
  const [salesPointLogos, setSalesPointLogos] = useState<string[]>();
  const [location, setLocation] = useState<Point>();

  useEffect(() => {
    setSalesPointLogos(getLogos(salesPoint?.value));
    setLocation(salesPoint?.value?.location);
  }, [salesPoint]);

  const openBrowser = (type: MapType) => {
    var url: string = null;
    switch (type) {
      case 'call': {
        const cleanPhone = salesPoint.value.phone.replace(/[\s\-()]/g, '');
        url = `tel:${cleanPhone}`;
        break;
      }
      case 'direction':
        url =
          location && `https://www.google.com/maps/dir/?api=1&destination=${location.latitude}%2C${location.longitude}`;
        break;
      case 'location':
        url = location && `https://www.google.com/maps/place/${location.latitude},${location.longitude}`;
        break;
    }

    if (!url) {
      return;
    }

    // tel: links work better with direct navigation on iOS
    if (type === 'call') {
      window.location.href = url;
    } else if (window.cordova) {
      window.cordova.InAppBrowser.open(url, '_system');
    } else {
      window.open(url, '_blank');
    }
  };

  const getDirectionsText = () => {
    var directionsText: string = t('salesPoint:directionsText');

    if (salesPoint.distanceInMeters) {
      directionsText += ` (${(salesPoint.distanceInMeters / 1000).toFixed(2)} km)`;
    }

    return directionsText;
  };

  const getLogos = (salesPointParam: SalesPoint) => {
    const logos: string[] = [];
    if (appStyleMode === AppStyle.Formada) {
      logos.push(FormadaLogo);
    } else if (salesPointParam?.offices.length > 0) {
      const officeLogos = salesPointParam.offices
        .filter((office) => office.logoId)
        .map((office) => `${imagesUri}/${office.logoId}?orderme-api-key=${apiKey}`);
      logos.push(...officeLogos);
    }

    return logos;
  };

  const gridStyleWebsite = {
    borderRadius: '0px 0px 4px 4px',
    maxWidth: '1200px',
    margin: 'auto',
    backgroundColor: 'white',
    color: 'black',
    pt: '10px',
    width: '100%',
  } as const;

  return (
    <>
      {salesPoint && (
        <Box
          className={appStyleMode === AppStyle.Mobile ? 'point-info' : null}
          sx={appStyleMode !== AppStyle.Mobile ? gridStyleWebsite : null}
        >
          <Grid container direction={{ xs: 'column', md: 'row' }} sx={{ justifyContent: 'stretch' }}>
            <Grid
              size="grow"
              sx={{
                width: 'auto',
                margin: '10px',
              }}
            >
              <Stack direction="column" justifyContent="space-around" alignItems="center">
                {salesPointLogos?.length > 0 && (
                  <Stack direction="row" justifyContent="center" sx={{ width: '100%' }}>
                    {salesPointLogos.map((logoUrl, index) => {
                      return (
                        <ImageLoader
                          key={index}
                          sx={{ margin: '5px', maxWidth: '100%', maxHeight: '120px' }}
                          src={logoUrl}
                          alt={salesPoint.value.name + ' ' + index}
                        />
                      );
                    })}
                  </Stack>
                )}
                {salesPoint.value.workingHoursInfo && (
                  <HtmlViewer content={salesPoint.value.workingHoursInfo.replace(/\n/g, '<br />')} />
                )}
              </Stack>
            </Grid>
            <Grid
              size="grow"
              sx={{
                mx: '10px',
              }}
            >
              <List>
                {location && (
                  <>
                    <CardElement
                      text={getDirectionsText()}
                      action={() => openBrowser('direction')}
                      icon={<DirectionsOutlinedIcon />}
                    />
                    <CardElement
                      text={salesPoint.value.name}
                      action={() => openBrowser('location')}
                      icon={<LocationOnIcon />}
                    />
                  </>
                )}
                <CardElement
                  text={t('salesPoint:call', { phone: salesPoint.value.phone })}
                  action={() => openBrowser('call')}
                  icon={<PhoneAndroidOutlinedIcon />}
                />
                {salesPoint.value.serviceStaff && salesPoint.value.serviceStaff.length > 0 && (
                  <CardElement
                    text={t('salesPoint:workingStaff', { staff: salesPoint.value.serviceStaff?.join() })}
                    isButton={false}
                    icon={<AccountCircleIcon />}
                  />
                )}
              </List>
              <Card sx={{ display: !salesPoint.value.info && 'none', mb: '10px' }}>
                <CardHeader
                  title={<Typography sx={{ textAlign: 'center' }}>{t('salesPoint:clientsInfo')}</Typography>}
                  sx={{ backgroundColor: 'card.primary.background', color: 'card.primary.color' }}
                />
                <CardContent>
                  <Box>
                    <Typography sx={{ textAlign: 'center' }}>{salesPoint.value.info}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}
